import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import { SMPLandingPageSkeleton } from './SMPLandingPageSkeleton';
import { SMPLandingPageHeader } from './sections/SMPLandingPageHeader';
import { SMPLandingPageNextOrder } from './sections/SMPLandingPageNextOrder';
import { SMPLandingPageOrderInProgress } from './sections/SMPLandingPageOrderInProgress';
import { SMPLandingPageManageSection } from './sections/SMPLandingPageManageSection';
import { SMPLandingPageShippingPayment } from './sections/SMPLandingPageShippingPayment';
import { SMPLandingPageOrders } from './sections/SMPLandingPageOrders';
import { SMPLandingPageInfo } from './sections/SMPLandingPageInfo';
import { SMPMutationErrorDialog, SMPMaintenanceBanner } from './components';
import { NavBar } from '@design-system/NavBar.tsx';
import { ACCOUNT_MANAGEMENT_PATH } from '@/constants/paths';
import { isSubscriptionPaused, formatSMPDate } from './utils/smpHelpers';
import { useUIMessages } from './hooks/useUIMessages';
import { SkipOrderBlockDialog, type SkipBlockReason } from './components/SkipOrderBlockDialog';

const NOD_PENDING = 'Pending';

export const SMPLandingPage = () => {
  const navigate = useNavigate();
  const {
    subscriptionDetail,
    productComponents,
    customerDiscount,
    hasRecommendation,
    hasPersonalizedSupplementRecommendations,
    isEligibleForSupplements,
    hasOrderInProgress,
    canSkip,
    isOnHoldingPatternState,
    showMaintenance,
    uiMessages,
    isLoading,
    isError,
    hasNonCriticalError,
  } = useSMPData();

  const { getMessageText } = useUIMessages(uiMessages);

  const [showNonCriticalErrorDialog, setShowNonCriticalErrorDialog] = useState(false);
  const [showSkipBlockDialog, setShowSkipBlockDialog] = useState(false);
  const [skipBlockReason, setSkipBlockReason] = useState<SkipBlockReason>(null);

  useEffect(() => {
    if (hasNonCriticalError) {
      setShowNonCriticalErrorDialog(true);
    }
  }, [hasNonCriticalError]);

  const handleSkipOrderClick = () => {
    if (!subscriptionDetail) return;

    const isPaused = isSubscriptionPaused(subscriptionDetail.subscription_status);
    const isNODPending = subscriptionDetail.next_order_datetime === NOD_PENDING;

    if (isPaused) {
      setSkipBlockReason('paused');
      setShowSkipBlockDialog(true);
    } else if (isNODPending) {
      setSkipBlockReason('nod-pending');
      setShowSkipBlockDialog(true);
    } else if (!canSkip) {
      setSkipBlockReason('already-skipped');
      setShowSkipBlockDialog(true);
    } else if (isOnHoldingPatternState) {
      setSkipBlockReason('holding-pattern');
      setShowSkipBlockDialog(true);
    } else {
      navigate('/smp/skip-next-order');
    }
  };

  if (isLoading) {
    return <SMPLandingPageSkeleton />;
  }

  if (isError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const { subscription_status } = subscriptionDetail;
  const blurredClass = showMaintenance ? 'blur-sm pointer-events-none select-none' : '';

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-landingPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          data-testid="navbar"
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={() => navigate(ACCOUNT_MANAGEMENT_PATH)}
        />

        {showMaintenance && <SMPMaintenanceBanner />}

        {/* Page Title */}
        <h1
          className="text-[32px] md:text-[40px] font-normal text-primary"
          data-testid="smp-pageTitle"
        >
          My Subscription
        </h1>

        {/* 1. Header Section – Plan details, status badge, product images */}
        <div className={blurredClass}>
          <SMPLandingPageHeader subscription={subscriptionDetail} productComponents={productComponents} customerDiscount={customerDiscount} />
        </div>

        {/* 2. Next Order Section – Date, info icon, "I need it sooner" */}
        <div className={blurredClass}>
          <SMPLandingPageNextOrder
            subscription={subscriptionDetail}
            hasRecommendation={hasRecommendation}
            hasOrderInProgress={hasOrderInProgress}
            uiMessages={uiMessages}
          />
        </div>

        {/* 3. Order in Progress Section – Status bar, order details link */}
        {hasOrderInProgress && (
          <div className={blurredClass}>
            <SMPLandingPageOrderInProgress />
          </div>
        )}
      </div>

      {/* 4. Manage Subscription Section — hidden during maintenance */}
      {!showMaintenance && (
        <SMPLandingPageManageSection
          subscriptionStatus={subscription_status}
          hasPersonalizedSupplementRecommendations={hasPersonalizedSupplementRecommendations}
          isEligibleForSupplements={isEligibleForSupplements}
          onSkipOrderClick={handleSkipOrderClick}
        />
      )}

      {/* 5. Shipping & Payment Section */}
      <div className={blurredClass}>
        <SMPLandingPageShippingPayment subscription={subscriptionDetail} />
      </div>

      {/* 6. Orders Section */}
      <div className={blurredClass}>
        <SMPLandingPageOrders subscriptionStatus={subscription_status} />
      </div>

      {/* 7. Information Section */}
      <SMPLandingPageInfo />

      <SkipOrderBlockDialog
        open={showSkipBlockDialog}
        reason={skipBlockReason}
        messageHtml={(() => {
          switch (skipBlockReason) {
            case 'paused': return getMessageText('smp_paused_msg', 'bodyHtml');
            case 'nod-pending': return getMessageText('smp_nod_pending_msg', 'bodyHtml');
            case 'already-skipped': return getMessageText('smp_already_skipped', 'bodyHtml', { skippedDate: formatSMPDate(subscriptionDetail.next_order_datetime) });
            case 'holding-pattern': return getMessageText('smp_holding_pattern_state_msg', 'bodyHtml');
            default: return '';
          }
        })()}
        ctaLabel={skipBlockReason === 'holding-pattern' ? getMessageText('smp_pause_subscription', 'headerText', undefined, 'Pause Subscription') : undefined}
        onCtaClick={skipBlockReason === 'holding-pattern' ? () => {
          setShowSkipBlockDialog(false);
          navigate('/smp/pause-subscription');
        } : undefined}
        onClose={() => setShowSkipBlockDialog(false)}
      />

      <SMPMutationErrorDialog
        open={showNonCriticalErrorDialog}
        onClose={() => setShowNonCriticalErrorDialog(false)}
        description="Some subscription information could not be loaded. You may see incomplete data on this page."
      />
    </section>
  );
};
